import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Music2 } from "lucide-react";
import { BRAND } from "../config";
import { useContent } from "../hooks/useContent";

const FOOTER_FALLBACK = {
  motto: BRAND.motto,
  support_email: "support@taven.store",
  copyright_name: "TAVEN SYSTEM LLC",
  social_instagram: "#", social_tiktok: "#", social_facebook: "#", social_youtube: "#",
};

const COLS = [
  { title: "Shop", links: [
    { label: "Trending Products", to: "/collection/trending-deals" },
    { label: "Best Sellers", to: "/collection/best-sellers" },
    { label: "Big Sales", to: "/collection/big-sales" },
    { label: "Luxury Essentials", to: "/collection/luxury-essentials" },
    { label: "Premium Beauty", to: "/collection/premium-beauty" },
    { label: "Home Favorites", to: "/collection/home-favorites" },
  ]},
  { title: "Opportunity", links: [
    { label: "Marketplace", to: "/marketplace" },
    { label: "Become a Seller", to: "/become-a-seller" },
    { label: "Affiliate Program", to: "/affiliate-program" },
    { label: "Affiliate Documents", to: "/affiliate-program/documents" },
    { label: "Education Hub", to: "/education" },
    { label: "Sell on Other Marketplaces", to: "/sell-on-other-marketplaces" },
  ]},
  { title: "Support", links: [
    { label: "Help Center", to: "/help" },
    { label: "About Us", to: "/about" },
    { label: "Contact", to: "/contact" },
    { label: "Legal Documentation Center", to: "/legal" },
    { label: "Shipping Policy", to: "/legal/shipping-policy" },
    { label: "Return Policy", to: "/policy/return" },
    { label: "Refund Policy", to: "/legal/refund-policy" },
    { label: "Privacy Policy", to: "/legal/privacy-policy" },
    { label: "Terms of Service", to: "/legal/terms-of-service" },
  ]},
];

const SOCIAL_ICONS = [
  { Icon: Instagram, key: "social_instagram", label: "Instagram" },
  { Icon: Music2, key: "social_tiktok", label: "TikTok" },
  { Icon: Facebook, key: "social_facebook", label: "Facebook" },
  { Icon: Youtube, key: "social_youtube", label: "YouTube" },
];

export const Footer = () => {
  const c = useContent("footer_content", FOOTER_FALLBACK);
  return (
  <footer className="bg-taven-black text-white pt-16 pb-24 lg:pb-10 mt-8" data-testid="site-footer">
    <div className="px-4 md:px-8 lg:px-12 mx-auto max-w-7xl grid grid-cols-2 lg:grid-cols-4 gap-8">
      <div className="col-span-2 lg:col-span-1">
        <span className="text-2xl font-black font-display tracking-tighter">TAVEN</span>
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-taven-gold mt-1">{BRAND.name}</p>
        <p className="text-sm text-white/60 mt-4 leading-relaxed">{c.motto}</p>
        <a href={`mailto:${c.support_email}`} className="text-sm text-taven-gold mt-3 inline-block hover:underline" data-testid="footer-support-email">{c.support_email}</a>
        <div className="flex gap-3 mt-5">
          {SOCIAL_ICONS.map(({ Icon, key, label }) => (
            <a key={label} href={c[key] || "#"} aria-label={label} className="p-2 border border-white/15 hover:border-taven-gold hover:text-taven-gold transition-colors"><Icon className="w-4 h-4" /></a>
          ))}
        </div>
      </div>
      {COLS.map((col) => (
        <div key={col.title}>
          <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-taven-gold mb-4">{col.title}</h4>
          <ul className="space-y-2.5">
            {col.links.map((l) => (
              <li key={l.label}><Link to={l.to} className="text-sm text-white/70 hover:text-white transition-colors">{l.label}</Link></li>
            ))}
          </ul>
        </div>
      ))}
    </div>
    <div className="px-4 md:px-8 lg:px-12 mx-auto max-w-7xl mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3">
      <p className="text-xs text-white/50">© {new Date().getFullYear()} {c.copyright_name}. All rights reserved.</p>
      <div className="flex gap-4">
        <Link to="/policy/earnings-disclaimer" className="text-xs text-white/50 hover:text-white">Earnings Disclaimer</Link>
        <Link to="/corporate-documents" className="text-xs text-white/50 hover:text-white">Corporate Documents</Link>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
